import React, {Fragment, useContext, useState} from "react";
import {Form, FormGroup} from "reactstrap";
import {useForm} from "react-hook-form";
import {Btn} from "../../../../../AbstractElements";
import Loader from "../../../../../Layout/Loader";
import axiosConfig from "../../../../../Config/axios";
import {getApplicationRoute} from "../../../../../Route/RouteIndex";
import AuthorizationContext from "../../../../../_helper/Authorization";
import type {AuthorizationContextType} from "../../../../../_helper/Authorization/AuthorizationTypes";
import TextInput from "../../../../../CommonElements/Input/TextInput";
import type {Board} from "../../Types/BoardTypes";

interface BoardWinnerFormValues {
    winning_number: number;
    participant_percentage?: number;
    system_percentage?: number;
    collector_percentage?: number;
}

interface BoardWinnerFormProps {
    board: Board;
    callBack?: () => void;
}

const WinnerForm: React.FC<BoardWinnerFormProps> = ({board, callBack}) => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler, authorizedUser} = authContext || {};
    const userRoles = (authorizedUser?.system_roles || []).map((role: string) => role?.toLowerCase());
    const isSuperAdminUser = userRoles.includes('super-admin');
    const isWinningNumberSet = board?.winning_number !== undefined && board?.winning_number !== null;

    const {
        register,
        handleSubmit,
        clearErrors,
        formState: {errors}
    } = useForm<BoardWinnerFormValues>({
        defaultValues: {
            winning_number: board.winning_number,
            participant_percentage: board.participant_percentage,
            system_percentage: board.system_percentage,
            collector_percentage: board.collector_percentage
        }
    });

    const onSubmit = (data: BoardWinnerFormValues) => {
        if (isWinningNumberSet) {
            return;
        }
        if (!isSuperAdminUser) {
            errorHandler('Only admin can mark winning numbers.');
            return;
        }
        clearErrors();
        setLoaderVisibility(true);
        axiosConfig
            .post(`${getApplicationRoute("board")}/${board.id}/winning-number`, data)
            .then(() => {
                callBack?.();
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    };

    return (
        <Fragment>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Loader visibility={loaderVisibility}/>
                {isWinningNumberSet && (
                    <div className='alert alert-info'>Winning number is already set for this board.</div>
                )}
                <TextInput name={'winning_number'} type={'number'} label={'Winning Number'} register={register}
                           errors={errors} placeholder={'0 - 99'} options={{required: true, min: 0, max: 99}}
                           disabled={isWinningNumberSet}/>
                <TextInput name={'participant_percentage'} type={'number'} label={'Winners Percentage'} register={register}
                           errors={errors} placeholder={'0 - 100'} options={{min: 0, max: 100}}
                           disabled={isWinningNumberSet}/>
                <TextInput name={'system_percentage'} type={'number'} label={'System Percentage'} register={register}
                           errors={errors} placeholder={'0 - 100'} options={{min: 0, max: 100}}
                           disabled={isWinningNumberSet}/>
                <TextInput name={'collector_percentage'} type={'number'} label={'Collector Percentage'} register={register}
                           errors={errors} placeholder={'0 - 100'} options={{min: 0, max: 100}}
                           disabled={isWinningNumberSet}/>
                <FormGroup>
                    <Btn attrBtn={{
                        className: "d-block w-100 mt-4",
                        color: "primary",
                        type: "submit",
                        disabled: isWinningNumberSet
                    }}>
                        {isWinningNumberSet ? 'Already Marked' : 'Submit'}
                    </Btn>
                </FormGroup>
            </Form>
        </Fragment>
    );
};

export default WinnerForm;

